// src/pages/TradingAILandingPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { getAuth } from 'firebase/auth';
import { trackEvent } from '../utils/analytics/trackEvent.js';
import Footer from '../components/Footer.jsx';
import Lottie from "lottie-react";
import tapAnimation from "../assets/animations/coin_sparkle.json";
import { FaChartLine, FaBullseye, FaChartArea, FaWater } from 'react-icons/fa';
import { askAhaMomentAI } from '../api/askAhaMomentAI.js';

const TradingAILanding = () => {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    trackEvent('aha_page_view', {
      screen: 'TradingAILanding',
      channel: 'web',
      timestamp: Date.now(),
    });
  }, []);

  const lightColors = ["#FFE5D0", "#D6F0FF", "#DFFFE2", "#F0E5FF", "#F9F9F9"];

  const suggestionGroups = [
    {
      title: "📊 Technicals",
      items: [
        { text: "Candlestick Patterns", icon: <FaChartLine /> },
        { text: "Moving Averages", icon: <FaChartArea /> },
        { text: "Breakouts", icon: <FaBullseye /> },
        { text: "Support & Resistance", icon: <FaChartLine /> },
      ]
    },
    {
      title: "📈 Price Action",
      items: [
        { text: "Smart Money Concepts", icon: <FaChartArea /> },
        { text: "Order Blocks", icon: <FaChartLine /> },
        { text: "Liquidity Grabs", icon: <FaWater /> },
        { text: "Change of Character", icon: <FaChartArea /> },
      ]
    },
    {
      title: "⚙️ Strategy",
      items: [
        { text: "Risk Management", icon: <FaBullseye /> },
        { text: "Trading Psychology", icon: <FaChartArea /> },
        { text: "Backtesting", icon: <FaChartLine /> },
        { text: "Stop Loss Placement", icon: <FaBullseye /> },
      ]
    },
    {
      title: "💥 Advanced",
      items: [
        { text: "Institutional Order Flow", icon: <FaChartLine /> },
        { text: "Liquidity Voids", icon: <FaWater /> },
        { text: "Fair Value Gaps", icon: <FaChartArea /> },
        { text: "Scalping vs Swing", icon: <FaBullseye /> },
      ]
    }
  ];

  const handleAskAI = async (overrideInput) => {
    const usermessage = (overrideInput || input).trim();
    if (!usermessage) return;
    setLoading(true);

    const chatSessionId = uuidv4();
    const user = auth.currentUser;
    const userId = user?.uid || 'anonymous';

    trackEvent('aha_ask_ai_button_clicked', {
      screen: 'TradingAILanding',
      user_id: userId,
      channel: 'web',
      message_length: usermessage.length,
      timestamp: Date.now(),
    });

    navigate(`/learn/trading/space/${chatSessionId}`, {
      state: { question: usermessage },
    });

    askAhaMomentAI({
      userId,
      chatsessionid: chatSessionId,
      usermessage,
      messageCount: 0,
    }).catch(console.warn);

    setLoading(false);
  };

  return (
    <>
      <div style={{ background: '#232830', padding: '0.8rem 1.2rem', display: 'flex', alignItems: 'center', height: '60px', position: 'fixed', top: 0, width: '100%', zIndex: 1000 }}>
        <img src="/assets/fintorro-logo.png" alt="FinTorro" style={{ height: '36px' }} />
      </div>

      <div style={{ background: '#232830', color: '#fff', minHeight: 'calc(100vh - 100px)', paddingTop: '80px', fontFamily: 'Nunito Sans, sans-serif' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 1rem' }}>
          <h1 style={{ fontSize: '2.4rem', fontWeight: '800', textAlign: 'center', color: '#FFFFFF'  }}>📊 Master Trading with AI</h1>
          <p style={{ fontSize: '1.2rem', textAlign: 'center', marginBottom: '2rem', color: '#FFFFFF' }}>
            From chart patterns to smart money concepts — just ask.
          </p>

          <div style={{ position: 'relative', marginBottom: '1rem' }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. What are liquidity grabs?"
              rows={5}
              style={{
                width: '100%', padding: '1rem', borderRadius: '16px', fontSize: '1.1rem',
                border: '1px solid #ccc', color: '#fff', backgroundColor: '#2C2F36'
              }}
            />
            <button
              onClick={() => handleAskAI()}
              style={{
                position: 'absolute', bottom: '12px', right: '12px',
                padding: '0.75rem 1.25rem', fontWeight: '700',
                backgroundColor: '#FF7C24', color: '#fff', borderRadius: '10px', border: 'none'
              }}
            >
              {loading ? 'Thinking...' : 'Ask AI'}
            </button>
          </div>

          <div style={{ display: 'grid', gap: '2rem' }}>
            {suggestionGroups.map((group, gIdx) => (
              <div key={gIdx}>
                <h3 style={{ color: '#fff', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {group.title}
                  <Lottie animationData={tapAnimation} loop style={{ width: 32, height: 32 }} />
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
                  {group.items.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleAskAI(item.text)}
                      style={{
                        background: lightColors[idx % lightColors.length],
                        color: '#232830',
                        borderRadius: '12px',
                        padding: '0.9rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease-in-out',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
                      onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                      <div style={{ fontSize: '1.6rem' }}>{item.icon}</div>
                      <div style={{ textAlign: 'center' }}>{item.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default TradingAILanding;